import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  AppBar,
  Typography,
  Button,
  Box,
  Breadcrumbs,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import { externalpostApi } from "../../services/externalApis.tsx";
import { ITEM_CREATE_VARIANT_TYPE } from "../../api/ApiModel.tsx";
import {
  CON_STATUS_ACTIVE,
  CON_DEFAULT_IMAGE_TYPE,
  CON_DEFAULT_ITEM_CODE,
} from "../../utils/sys/Constants.tsx";
import {
  MSG_ITEM_VALIDATION_REQUIRED_FIELDS,
  MSG_ITEM_NAVIGATION_ERROR,
} from "../../utils/sys/Messages.tsx";

// Import custom hooks
import { useDropdownData } from "../../hooks/useDropdownData.tsx";
import { useItemForm } from "../../hooks/useItemForm.tsx";
import { useVariants } from "../../hooks/useVariants.tsx";
import AddVariantTypeDialog from "../../components/items/AddVariantTypeDialog.tsx";

// Import components
import ItemFormFields from "../../components/items/ItemFormFields.tsx";
import VariantSection from "../../components/items/VariantSection.tsx";
import ImageTypeSelector from "../../components/items/ImageTypeSelector.tsx";
import SystemAlert from "../../components/common/SystemAlert.tsx";

// Types and Interfaces
interface User {
  USER_PARAM?: {
    COMPCODE?: string;
    STAFFMAPLOCODE?: string;
    USERNAME?: string;
  };
}

interface RootState {
  user: User;
}

interface LocationState {
  selectedItem?: ApiItem;
  actionText?: string;
  itemData?: TransformedItem;
  formData?: Partial<FormData>;
  variants?: Variant[];
  imageType?: string;
  returnFromItemImages?: boolean;
}

interface ApiItem {
  itemcode?: string | number;
  itemname?: string;
  variantdata?: string;
  endate?: string;
  uptype?: string;
  sts?: string;
  modelname?: string;
  brandcode?: string | number;
  maincatcode?: string | number;
  catcode?: string | number;
  subcatcode?: string | number;
  genericcode?: string | number;
  packagecode?: string | number;
  measuringunitcode?: string | number;
  packageunits?: number;
  descrip?: string;
  imagetype?: string;
  maincatname?: string;
  catname?: string;
  subcatname?: string;
  genericname?: string;
  brandname?: string;
  enuser?: string;
  [key: string]: any;
}

interface TransformedItem {
  id: string | number;
  itemcode: string;
  itemname: string;
  varianttype: string;
  createddate: string;
  uptype: string;
  status: string;
  modelname: string;
  brandcode: string;
  maincatcode: string;
  catcode: string;
  subcatcode: string;
  genericcode: string;
  packagecode: string;
  measuringunitcode: string;
  packageunits: number;
  descrip: string;
  originalData: ApiItem;
}

// Single FormData interface that matches what useItemForm returns AND what ItemFormFields expects
interface FormData {
  brand: string;
  mainCategory: string;
  category: string;
  subCategory: string;
  genericName: string;
  itemName: string;
  uom: string;
  packageType: string;
  unitsPerPackage: string;
  description: string;
  modelName: string;
}

interface FormErrors {
  brand?: string;
  mainCategory?: string;
  category?: string;
  subCategory?: string;
  genericName?: string;
  packageType?: string;
  uom?: string;
  itemName?: string;
  modelName?: string;
  unitsPerPackage?: string;
  description?: string;
}

interface Variant {
  variantcode?: number | string;
  variantname: string;
  variantvalue: string;
  variantName?: string;
  variantValue?: string;
  values?: string[];
}

interface DropdownOption {
  value: string | number;
  label: string;
}

// Define the expected variant type from useDropdownData
interface DropdownVariantType {
  value: string | number;
  label: string;
  varianttype?: string;
  variantname?: string;
  variantvalue?: string;
  code?: string | number;
  id?: string | number;
  name?: string;
  type?: string;
}

// Define the variant type expected by useVariants
interface VariantsHookVariantType {
  code: string;
  value: string;
  label: string;
  varianttype: string;
  variantname: string;
  variantvalue: string;
}

interface AddVariantPayload {
  VARIANTCODE: number;
  COMPCODE: string;
  LOCCODE: string;
  VARIANTNAME: string;
  VARIANTDATATYPE: string;
  STS: string;
  ENUSER: string;
  ENDATE: string;
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
    status?: number;
    statusText?: string;
  };
  request?: any;
  message?: string;
}

interface AddVariantResult {
  success: boolean;
  message: string;
}

function CreateItem(): React.ReactElement {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState || {};

  // Get user from Redux store
  const SYS_USER: User = useSelector((state: RootState) => state.user) || {};
  const { COMPCODE, STAFFMAPLOCODE, USERNAME } = SYS_USER.USER_PARAM || {};

  // Get edit data or return-from-item-images state from navigation
  const {
    selectedItem,
    actionText = "Add",
    itemData,
    formData: stateFormData,
    variants: stateVariants,
    imageType: stateImageType,
    returnFromItemImages,
  } = locationState;

  const isEditMode: boolean = actionText === "Edit";
  const editItemId: string | number =
    selectedItem?.itemcode || itemData?.itemcode || CON_DEFAULT_ITEM_CODE;

  // Custom hooks
  const {
    isFetchingData,
    brands,
    mainCategories,
    categories,
    subCategories,
    genericNames,
    uoms,
    packageTypes,
    variantTypes: dropdownVariantTypes,
    error: dropdownError,
    fetchCategories,
    fetchSubCategories,
    fetchGenericNames,
    refetch: refetchDropdownData,
  } = useDropdownData(COMPCODE, STAFFMAPLOCODE);

  // Convert variant types to the format expected by useVariants
  const convertedVariantTypes = useMemo((): VariantsHookVariantType[] => {
    if (!dropdownVariantTypes || !Array.isArray(dropdownVariantTypes)) {
      return [];
    }

    return dropdownVariantTypes.map((item: DropdownVariantType, index: number) => {
      // Generate a code from available properties
      const code =
        item.code ||
        item.id ||
        item.value ||
        `temp-${index}-${Date.now()}`;

      // Get value as string
      const value = item.value?.toString() || "";

      // Get label from various possible sources
      const label =
        item.label ||
        item.variantname ||
        item.name ||
        value ||
        "Unknown";

      return {
        code: code.toString(),
        value: value,
        label: label,
        varianttype: item.varianttype || item.type || value,
        variantname: item.variantname || item.label || item.name || label,
        variantvalue: item.variantvalue || value,
      };
    });
  }, [dropdownVariantTypes]);

  const {
    formData,
    errors,
    setFormData,
    handleInputChange,
    handleTextInputChange,
    handleTextBlur,
    handleTextPaste,
    handleDescriptionChange,
    handleUnitsPerPackageChange,
    validateForm,
    populateFormWithEditData,
    resetForm,
  } = useItemForm(isEditMode);

  const {
    variants,
    variantTypes,
    newVariantName,
    newVariantValue,
    setNewVariantName,
    setNewVariantValue,
    setVariants,
    parseVariantData,
    handleAddNewVariant,
    handleRemoveVariantValue,
    handleRemoveVariant,
    canRemoveVariantValue,
    canRemoveVariantType,
    handleAddVariantType,
    combinationCount,
    combinations,
  } = useVariants(isEditMode, convertedVariantTypes);

  // Consolidated loading states
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [loadingEditData, setLoadingEditData] = useState<boolean>(false);
  const isLoading: boolean = isFetchingData || loadingEditData || isSubmitting;

  // Alert state
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error" | "warning" | "info">("success");
  const [alertMessage, setAlertMessage] = useState<string>("");

  // State for Add New Variant dialog
  const [showAddVariant, setShowAddVariant] = useState<boolean>(false);
  const [dialogVariantName, setDialogVariantName] = useState<string>("");

  // Image state
  const [imageType, setImageType] = useState<string>(CON_DEFAULT_IMAGE_TYPE);
  const [commonImages] = useState<any[]>([]);

  // Once we've restored from item-images, never clear category/subcategory/generic so they don't "disappear"
  const restoredFromItemImagesRef = useRef<boolean>(false);

  // Auto-populate Category when Brand + Main Category are selected (skip clear when returning from item-images or already restored)
  useEffect(() => {
    if (formData.brand && formData.mainCategory) {
      fetchCategories(formData.mainCategory);
    } else if (
      !formData.mainCategory &&
      !(returnFromItemImages && stateFormData) &&
      !restoredFromItemImagesRef.current
    ) {
      fetchCategories("");
      fetchSubCategories("", "");
      fetchGenericNames("", "");
      handleInputChange("category", "");
      handleInputChange("subCategory", "");
      handleInputChange("genericName", "");
    }
  }, [
    formData.brand,
    formData.mainCategory,
    returnFromItemImages,
    stateFormData,
    fetchCategories,
    fetchSubCategories,
    fetchGenericNames,
    handleInputChange,
  ]);

  // Auto-populate Subcategory when Brand + Main Category + Category are selected
  useEffect(() => {
    if (formData.brand && formData.mainCategory && formData.category) {
      fetchSubCategories(formData.category, formData.mainCategory);
    } else if (
      !formData.category &&
      !(returnFromItemImages && stateFormData) &&
      !restoredFromItemImagesRef.current
    ) {
      fetchSubCategories("", "");
      fetchGenericNames("", "");
      handleInputChange("subCategory", "");
      handleInputChange("genericName", "");
    }
  }, [
    formData.brand,
    formData.mainCategory,
    formData.category,
    returnFromItemImages,
    stateFormData,
    fetchSubCategories,
    fetchGenericNames,
    handleInputChange,
  ]);

  // Auto-populate Generic Name when Brand + Main Category + Subcategory are selected
  useEffect(() => {
    if (formData.brand && formData.mainCategory && formData.subCategory) {
      fetchGenericNames(formData.subCategory, formData.mainCategory);
    } else if (
      !formData.subCategory &&
      !(returnFromItemImages && stateFormData) &&
      !restoredFromItemImagesRef.current
    ) {
      fetchGenericNames("", "");
      handleInputChange("genericName", "");
    }
  }, [
    formData.brand,
    formData.mainCategory,
    formData.subCategory,
    returnFromItemImages,
    stateFormData,
    fetchGenericNames,
    handleInputChange,
  ]);

  // Restore form and variants when returning from item-images (Back button) - run only once per return
  const hasRestoredFormRef = useRef<boolean>(false);
  useEffect(() => {
    if (!returnFromItemImages || !stateFormData) {
      if (!returnFromItemImages) {
        hasRestoredFormRef.current = false;
        restoredFromItemImagesRef.current = false;
      }
      return;
    }
    if (hasRestoredFormRef.current) return;
    hasRestoredFormRef.current = true;
    restoredFromItemImagesRef.current = true;

    // Cast stateFormData to FormData since it should match the structure
    setFormData(stateFormData as FormData);

    if (stateVariants && stateVariants.length > 0) {
      parseVariantData(stateVariants);
    }
    if (stateImageType) {
      setImageType(stateImageType);
    }
  }, [
    returnFromItemImages,
    stateFormData,
    stateVariants,
    stateImageType,
    setFormData,
    parseVariantData,
  ]);

  useEffect(() => {
    if (!returnFromItemImages) {
      restoredFromItemImagesRef.current = false;
      hasRestoredFormRef.current = false;
    }
  }, [returnFromItemImages]);

  // Track whether we've loaded dependent dropdowns for "return from item-images" (run only once when meta is ready)
  const hasLoadedDependentDropdownsRef = useRef<boolean>(false);

  useEffect(() => {
    if (!returnFromItemImages) {
      hasLoadedDependentDropdownsRef.current = false;
    }
  }, [returnFromItemImages]);

  // Load category/subcategory/generic dropdown options AFTER meta is loaded (so they display when returning from item-images)
  useEffect(() => {
    if (
      !returnFromItemImages ||
      !stateFormData ||
      isFetchingData ||
      mainCategories.length === 0
    ) {
      return;
    }
    if (hasLoadedDependentDropdownsRef.current) {
      return;
    }
    hasLoadedDependentDropdownsRef.current = true;
    // Defer to next tick so itemMetaRef in useDropdownData is definitely populated
    const t = setTimeout(() => {
      if (stateFormData.mainCategory) {
        fetchCategories(stateFormData.mainCategory);
      }
      if (stateFormData.mainCategory && stateFormData.category) {
        fetchSubCategories(stateFormData.category, stateFormData.mainCategory);
      }
      if (stateFormData.mainCategory && stateFormData.subCategory) {
        fetchGenericNames(stateFormData.subCategory, stateFormData.mainCategory);
      }
    }, 0);
    return () => clearTimeout(t);
  }, [
    returnFromItemImages,
    stateFormData,
    isFetchingData,
    mainCategories.length,
    fetchCategories,
    fetchSubCategories,
    fetchGenericNames,
  ]);

  // Populate form when edit data is received (from Edit flow, not from item-images)
  useEffect(() => {
    if (isEditMode && selectedItem && !returnFromItemImages) {
      const populateEditData = async (): Promise<void> => {
        setLoadingEditData(true);
        try {
          populateFormWithEditData(selectedItem);
          if (selectedItem.variantdata) {
            parseVariantData(selectedItem.variantdata);
          }
          if (selectedItem.imagetype) {
            setImageType(selectedItem.imagetype);
          }
        } finally {
          setLoadingEditData(false);
        }
      };
      populateEditData();
    }
  }, [
    isEditMode,
    selectedItem,
    returnFromItemImages,
    populateFormWithEditData,
    parseVariantData,
  ]);

  // Load category/subcategory/generic options when editing so dropdowns display saved values.
  // Run only after Itemmeta has loaded; use staggered delays so each level is ready before the next.
  const hasLoadedEditDropdownsRef = useRef<boolean>(false);
  const editDropdownTimersRef = useRef<NodeJS.Timeout[]>([]);

  useEffect(() => {
    if (!returnFromItemImages) hasLoadedEditDropdownsRef.current = false;
  }, [returnFromItemImages]);

  useEffect(() => {
    if (
      !isEditMode ||
      !selectedItem ||
      returnFromItemImages ||
      mainCategories.length === 0 ||
      hasLoadedEditDropdownsRef.current
    )
      return;
    const main = selectedItem.maincatcode;
    const cat = selectedItem.catcode;
    const sub = selectedItem.subcatcode;
    if (!main) return;

    hasLoadedEditDropdownsRef.current = true;
    const timers = editDropdownTimersRef.current;

    // Wait 500ms for meta + form to settle, then load categories
    const t1 = setTimeout(() => {
      fetchCategories(main?.toString() || "");
      const t2 = setTimeout(() => {
        if (main && cat)
          fetchSubCategories(cat?.toString() || "", main?.toString() || "");
        const t3 = setTimeout(() => {
          if (sub)
            fetchGenericNames(sub?.toString() || "", main?.toString() || "");
        }, 300);
        timers.push(t3);
      }, 300);
      timers.push(t2);
    }, 500);
    timers.push(t1);

    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.length = 0;
    };
  }, [
    isEditMode,
    selectedItem,
    returnFromItemImages,
    mainCategories.length,
    fetchCategories,
    fetchSubCategories,
    fetchGenericNames,
  ]);

  // Show dropdown error alert
  useEffect(() => {
    if (dropdownError) {
      setAlertType("error");
      setAlertMessage(dropdownError);
      setAlertOpen(true);
    }
  }, [dropdownError]);

  // Prevent typing in dropdown fields
  const handleKeyDownOnDropdown = useCallback((e: React.KeyboardEvent) => {
    if (
      e.key !== "Tab" &&
      e.key !== "Escape" &&
      e.key !== "Enter" &&
      !e.ctrlKey &&
      !e.metaKey
    ) {
      e.preventDefault();
    }
  }, []);

  // User can always add variant types; each type is limited to one value (one combination per item)
  const canAddVariants: boolean = true;

  const handleAddVariantFromDialog = useCallback(async (): Promise<void> => {
    if (!dialogVariantName?.trim()) {
      setAlertType("error");
      setAlertMessage("Please enter a variant name");
      setAlertOpen(true);
      return;
    }

    const nameLower = dialogVariantName.trim().toLowerCase();
    const isDuplicate = convertedVariantTypes?.some(
      (row: VariantsHookVariantType) =>
        row.variantname.toString().trim().toLowerCase() === nameLower
    );

    if (isDuplicate) {
      setAlertType("error");
      setAlertMessage("This variant name already exists.");
      setAlertOpen(true);
      return;
    }

    // Show loading state
    setAlertType("info");
    setAlertMessage("Adding variant type...");
    setAlertOpen(true);

    const payload: AddVariantPayload = {
      VARIANTCODE: 0,
      COMPCODE: COMPCODE || "",
      LOCCODE: STAFFMAPLOCODE || "",
      VARIANTNAME: dialogVariantName.trim(),
      VARIANTDATATYPE: "Text", // Default value for variant type
      STS: CON_STATUS_ACTIVE,
      ENUSER: USERNAME || "",
      ENDATE: new Date().toISOString().slice(0, 10),
    };

    try {
      console.log("Saving variant type with payload:", payload);
      const response = await externalpostApi(ITEM_CREATE_VARIANT_TYPE, payload);
      console.log("Variant type saved successfully:", response);

      // Add to local state through handleAddVariantType
      handleAddVariantType(dialogVariantName.trim(), "Text");

      // Clear dialog state
      setDialogVariantName("");
      setShowAddVariant(false);

      // ❌ FIX: Do NOT call refetchDropdownData() here.
      //
      // Calling `await refetchDropdownData()` resets the whole dropdown meta in
      // useDropdownData, which re-triggers the three cascading "clear" useEffects
      // above while their option lists are momentarily empty. That was wiping
      // Category / Subcategory / Generic Name (even though Brand and Main Category
      // stayed) right after a variant type was created.
      //
      // The newly-created variant type is already merged into local state via
      // handleAddVariantType(), so the VariantSection dropdown reflects it
      // immediately without any destructive refresh.
      // await refetchDropdownData();

      // Show success message
      setAlertType("success");
      setAlertMessage("Variant type added successfully!");
      setAlertOpen(true);
    } catch (err: unknown) {
      const error = err as ApiError;
      console.error("Createitemvarianttype error:", error);

      // Show error message
      setAlertType("error");
      setAlertMessage(
        error?.response?.data?.message || "Failed to create variant type."
      );
      setAlertOpen(true);
    }
  }, [
    dialogVariantName,
    handleAddVariantType,
    COMPCODE,
    STAFFMAPLOCODE,
    USERNAME,
    convertedVariantTypes,
  ]);

  const handleAddNewVariantWithAlert = useCallback((): void => {
    const result = handleAddNewVariant() as AddVariantResult;
    if (!result.success) {
      setAlertType("error");
      setAlertMessage(result.message);
      setAlertOpen(true);
    }
  }, [handleAddNewVariant]);

  const handleSubmit = useCallback(async (): Promise<void> => {
    // Validate form
    const { valid } = validateForm();
    if (!valid) {
      setAlertType("error");
      setAlertMessage(MSG_ITEM_VALIDATION_REQUIRED_FIELDS);
      setAlertOpen(true);
      return;
    }

    setIsSubmitting(true);

    try {
      // Navigate directly to image upload page without calling Item POST API
      // Create trimmed versions for navigation
      const navigatedItemName = formData.itemName.trim();
      const navigatedDescription = formData.description.trim();

      navigate("/item-images", {
        state: {
          formData: {
            ...formData,
            itemName: navigatedItemName,
            description: navigatedDescription,
          },
          variants,
          variantCombinations: combinations,
          combinationCount,
          commonImages,
          imageType,
          itemId: editItemId || CON_DEFAULT_ITEM_CODE,
          isEditMode: isEditMode,
          selectedItem: selectedItem,
          savedItemData: selectedItem || null,
        },
      });
    } catch (error) {
      console.error("Error navigating to image upload:", error);
      setAlertType("error");
      setAlertMessage(MSG_ITEM_NAVIGATION_ERROR);
      setAlertOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    validateForm,
    isEditMode,
    editItemId,
    formData,
    variants,
    combinations,
    combinationCount,
    commonImages,
    imageType,
    selectedItem,
    navigate,
  ]);

  const handleCancel = useCallback((): void => {
    // Clear all form data
    resetForm();

    // Clear variants
    setVariants([]);

    // Reset image type
    setImageType(CON_DEFAULT_IMAGE_TYPE);

    // Clear variant dialog states
    setShowAddVariant(false);
    setDialogVariantName("");
    setNewVariantName("");
    setNewVariantValue("");

    // Clear any alerts
    setAlertOpen(false);
    setAlertMessage("");

    // Navigate back to previous page (items list)
    navigate("/items");
  }, [
    resetForm,
    setVariants,
    setImageType,
    setShowAddVariant,
    setDialogVariantName,
    setNewVariantName,
    setNewVariantValue,
    setAlertOpen,
    setAlertMessage,
    navigate,
  ]);

  if (loadingEditData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading...</Typography>
      </Box>
    );
  }

  const appBarTitle: string = isEditMode
    ? `Edit Item: ${selectedItem?.itemname || ""}`
    : "Add Item";

  return (
    <Box sx={styles.box_add_page_container as any}>
      <SystemAlert
        open={alertOpen}
        type={alertType}
        message={alertMessage}
        onClose={() => setAlertOpen(false)}
      />

      <Box sx={styles.box_breadcrumb as any}>
        <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
          <Tooltip title="Back to Item List" arrow>
            <IconButton
              sx={styles.btn_breadcrumb_back as any}
              onClick={() => navigate("/items")}
            >
              <ArrowBackIcon sx={styles.btn_breadcrumb_back_icon as any} />
            </IconButton>
          </Tooltip>
          <Typography sx={styles.breadcrumb_divider as any} ml={1}>
            |
          </Typography>
          <Breadcrumbs separator="›" sx={styles.breadcrumb as any}>
            <Typography sx={styles.breadcrumb_link as any}>
              Home
            </Typography>
            <Typography sx={styles.breadcrumb_link as any}>
              Item Management
            </Typography>
            <Typography sx={styles.breadcrumb_link_page as any}>
              {isEditMode ? "Edit Item" : "Add Item"}
            </Typography>
          </Breadcrumbs>
        </Box>
      </Box>

      <AppBar sx={styles.appbar as any}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Typography sx={styles.appbar_title as any}>{appBarTitle}</Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* Only show Create New Variant button when NOT in edit mode */}
            {!isEditMode && (
              <Button
                variant="contained"
                sx={{ ...styles.btn_primary as any, ...styles.btn_add_confirm as any }}
                // onClick={() => setShowAddVariant(true)}
                onClick={() => navigate("/vAddVariant")}
              >
                Create New Variant
              </Button>
            )}
          </Box>
        </Box>
      </AppBar>

      <Box
        component="form"
        noValidate
        autoComplete="off"
        sx={styles.add_form_page_container as any}
      >
        {isFetchingData ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading dropdown data...</Typography>
          </Box>
        ) : (
          <>
            <ItemFormFields
              formData={formData}
              errors={errors}
              brands={brands}
              mainCategories={mainCategories}
              categories={categories}
              subCategories={subCategories}
              genericNames={genericNames}
              uoms={uoms}
              packageTypes={packageTypes}
              handleInputChange={handleInputChange}
              handleTextInputChange={handleTextInputChange}
              handleTextBlur={handleTextBlur}
              handleTextPaste={handleTextPaste}
              handleDescriptionChange={handleDescriptionChange}
              handleUnitsPerPackageChange={handleUnitsPerPackageChange}
              handleKeyDownOnDropdown={handleKeyDownOnDropdown}
              isEditMode={isEditMode}
            />

            <VariantSection
              variants={variants}
              variantTypes={variantTypes}
              newVariantName={newVariantName}
              newVariantValue={newVariantValue}
              showAddVariant={showAddVariant}
              dialogVariantName={dialogVariantName}
              setNewVariantName={setNewVariantName}
              setNewVariantValue={setNewVariantValue}
              setShowAddVariant={setShowAddVariant}
              setDialogVariantName={setDialogVariantName}
              handleAddNewVariant={handleAddNewVariantWithAlert}
              handleRemoveVariantValue={handleRemoveVariantValue}
              handleRemoveVariant={handleRemoveVariant}
              canRemoveVariantValue={canRemoveVariantValue}
              canRemoveVariantType={canRemoveVariantType}
              handleAddVariantType={handleAddVariantType}
              handleAddVariantFromDialog={handleAddVariantFromDialog}
              handleKeyDownOnDropdown={handleKeyDownOnDropdown}
              combinationCount={combinationCount}
              canAddVariants={canAddVariants}
              isEditMode={isEditMode}
              styles={styles}
              existingVariantTypesFromApi={convertedVariantTypes}
              loadingVariantTypes={false}
            />

            <ImageTypeSelector
              imageType={imageType}
              setImageType={setImageType}
            />
          </>
        )}
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          mt: 3,
          px: 3,
          pb: 3,
        }}
      >
        <Button
          variant="outlined"
          sx={{ ...styles.btn_secondary as any, ...styles.btn_add_cancel as any }}
          onClick={handleCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ ...styles.btn_primary as any, ...styles.btn_add_confirm as any }}
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isSubmitting ? <CircularProgress size={24} /> : "Next"}
        </Button>
      </Box>

      {/* Add Variant Type Dialog */}
      <AddVariantTypeDialog
        open={showAddVariant}
        onClose={() => setShowAddVariant(false)}
        existingVariantTypes={convertedVariantTypes}
        loading={false}
        variantName={dialogVariantName}
        onVariantNameChange={setDialogVariantName}
        onAdd={handleAddVariantFromDialog}
        styles={styles}
      />
    </Box>
  );
}

export default CreateItem;